// @flow

import React, {Fragment} from 'react';
import classNames from 'classnames';
import InstantBookIcon from '../InstantBookIcon/InstantBookIcon';
import {FormattedMessage} from 'react-intl';
import type {ListingShape} from '../Hit/Hit';
import type {IntlShape} from 'react-intl';
import type {Node} from 'react';
import './DualPrice.less';
import type {PriceSummaryShape} from '../Price/Price';

export type PriceItemShape = {
    ...PriceSummaryShape,
    roundedFormattedAmount: ?string,
    isPrimary: boolean,
    isPriceBold: boolean
};

export type PriceLabelShape = {
    priceTypeId: string,
    pricePeriodDescription: string
};

export type ListingPricesShape = {
    nightlyPrice: PriceItemShape,
    totalPrice: PriceItemShape,
    priceLabel: PriceLabelShape
};

const listingHasPriceInfo = (listing: ListingShape): boolean => {
    const {priceSummary, priceSummarySecondary, priceLabel} = listing;

    return (
        priceSummary !== null ||
        priceSummarySecondary !== null ||
        priceLabel !== null
    );
};

const getListingPrices = (listing: ListingShape): ListingPricesShape => {
    const defaultPrice = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: '',
        formattedAmount: '',
        roundedFormattedAmount: ''
    };
    const defaultLabel = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: ''
    };
    const listingPriceSummary = listing.priceSummary || defaultPrice;
    const listingPriceSummarySecondary =
        listing.priceSummarySecondary || defaultPrice;
    const listingPriceSummaryId = listingPriceSummary.priceTypeId;
    const listingPriceSummarySecondaryId =
        listingPriceSummarySecondary.priceTypeId;
    const priceLabel = listing.priceLabel || defaultLabel;
    let nightlyPrice = {};
    let totalPrice = {};

    if (
        listingPriceSummaryId === 'PER_NIGHT' ||
        listingPriceSummarySecondaryId === 'UNKNOWN'
    ) {
        nightlyPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true
        };
        totalPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false
        };
    } else {
        totalPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true
        };
        nightlyPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false
        };
    }

    return {
        nightlyPrice,
        totalPrice,
        priceLabel
    };
};

export const DualPrice = ({
    listing,
    intl,
    isDesktop = true
}: {
    listing: ListingShape,
    intl: IntlShape,
    isDesktop: boolean
}) => {
    if (!listingHasPriceInfo(listing)) {
        return (
            <span
                className="Price Price--noPrice"
                data-wdio="Price"
                data-price="0"
            >
                <FormattedMessage
                    id={'ratesNone'}
                    description={'no price'}
                    defaultMessage={'View details for price'}
                >
                    {(txt: Node): Node => (
                        <span className="Price__Fallback">{txt}</span>
                    )}
                </FormattedMessage>
            </span>
        );
    }
    const {instantBookable} = listing;
    const {nightlyPrice, totalPrice, priceLabel} = getListingPrices(listing);

    const renderPriceItem = (item, key) => {
        const {
            pricePeriodDescription,
            formattedAmount,
            roundedFormattedAmount,
            isPrimary
        } = item;
        const isInsantBookable = instantBookable && isPrimary;
        if (
            !pricePeriodDescription &&
            !formattedAmount &&
            !roundedFormattedAmount
        ) {
            return null;
        }

        const styleClass = classNames('', {
            DualPrice__primary: item.isPrimary,
            DualPrice__secondary: !item.isPrimary,
            'DualPrice__main-price': item.isPriceBold
        });

        return (
            <span key={key} className={styleClass}>
                {isInsantBookable && (
                    <span className="DualPrice__instantBook_icon">
                        <InstantBookIcon intl={intl} />
                    </span>
                )}
                <span className="DualPrice__amount">
                    {roundedFormattedAmount || formattedAmount}
                </span>
                <span className="DualPrice__period">
                    {pricePeriodDescription}
                </span>
            </span>
        );
    };

    let prices = [nightlyPrice, totalPrice];
    // for mobile view primary item should always be first
    if (!isDesktop && !prices[0].isPrimary) {
        prices = prices.reverse();
    }
    return (
        <Fragment>
            {prices.map((price, index) =>
                renderPriceItem(price, `price_${index}`)
            )}
            <span className="DualPrice__priceLabel">
                {priceLabel.pricePeriodDescription}
            </span>
        </Fragment>
    );
};

export default DualPrice;
